import React, { useEffect, useState, Fragment, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Grid, Row, Column } from '@lux/components';
import { connect, useDispatch } from 'react-redux';
import Sticky from 'react-sticky-el';
import { GatsbyImage } from 'gatsby-plugin-image';
import {
  planActions,
  authActions,
  rootActions,
  accessoryActions
} from '@detox/actions';

import { BodySecondary, H1 } from '../Base';
import CisPointOfEntry from '../CisPointOfEntry';
import PhonesList from '../PhonesList';
import LoadingOverlay from '../LoadingOverlay';
import PhoneCatalogFilterSection from '../PhoneCatalogFilterSection';
import PlanSelect from '../PlanSelect';
import PromoBanner from '../PromoBanner';
import PromoStrip from '../PromoStrip';
import Spacing from '../Spacing';
import PersistStatusConsumer from '../PersistStatusConsumer';

import createDescription from '../../helpers/create-description';
import flattenNodes from '../../helpers/flatten-nodes';
import formatPrice from '../../helpers/formatPrice';
import getAemComponentByName from '../../helpers/getAemComponentByName';

import FILTERS from '../../config/filters';

import Theme from '../../theme';

import IconFilter from '../../assets/svgs/filter.svg';
import { getUrlParams } from '@lux/helpers';
import slugify from '../../helpers/slugify';
import isSimOnlyPlan from '../../helpers/is-sim-only-plan';
import {
  getFiltersFromQueryParams,
  getPhonesWithPrice,
  getUrlByPlan,
  sortByRankingAndTitle,
  updateQueryFilters
} from '../../helpers/catalogue-helpers';
import { CATALOGUE } from '../../constants';
import { ACTION_TYPES as APP_ACTION_TYPES } from '../../constants/actions';

const StyledSection = styled.div`
  background-color: ${p => p.theme.colours.grey_50};
  padding-top: ${p => p.theme.spacing(1)};
  ${p => p.theme.media.md`
    padding-top: ${p.theme.spacing(4)};
    padding-bottom: ${p.theme.spacing(9)};
  `};
`;

const StyledGrid = styled(props => <Grid {...props} />)`
  padding: 0;
  ${p => p.theme.media.md`
    padding: 0 ${p.theme.spacing(1)};
  `};
`;

const StyledRow = styled(props => <Row {...props} />)`
  margin: 0;
`;

const StyledColumn = styled(props => <Column {...props} />)`
  padding: 0;
`;

const StyledSticky = styled(props => <Sticky {...props} />)`
  position: relative;
  background-color: ${p => p.theme.colours.white};
  z-index: 9;
`;

const StyledFilterButton = styled(BodySecondary)`
  cursor: pointer;
`;

const StyledIconFilter = styled(props => <IconFilter {...props} />)`
  vertical-align: middle;
  margin-left: ${p => p.theme.spacing(0.5)};
`;

const FEATURE_DISABLED = false;

// PlanSelect requires items to have text and value.
const withTextAndValue = plan => ({
  ...plan,
  text: plan.planName,
  value: plan.mecId
});

// Strip values so we don't keep the extra values in redux store
const stripValues = plan => {
  const planClone = { ...plan };
  delete planClone['text'];
  delete planClone['value'];
  delete planClone['selected'];
  delete planClone['id'];
  return planClone;
};

export const PhoneCatalog = ({
  data,
  selectedPlan,
  setSelectedPlan,
  authStatus,
  deleteWorryFree,
  resetByKeys,
  cisPlanRates,
  cisLoading,
  previewMode,
  userSelection = {},
  location = {},
  isCisPage = false,
  clearSelectedAccessories
}) => {
  let {
    allPhone,
    allPlan,
    allPlanGroup,
    allPhoneCatalogCategory,
    allAemPages,
    allPhoneBrand,
    allInstalmentOptions = null
  } = data;

  // Flatten list of plans to ease in searching for filters
  allPlan = flattenNodes(allPlan);
  allPlanGroup = useMemo(() => flattenNodes(allPlanGroup), [allPlanGroup]);
  allPhoneBrand = flattenNodes(allPhoneBrand).map(b => b.text);
  allPhoneCatalogCategory = flattenNodes(allPhoneCatalogCategory);

  if (allInstalmentOptions) {
    allInstalmentOptions = flattenNodes(allInstalmentOptions);
  }

  const allSimOnlyPlusPlan = allPlan.filter(
    plan =>
      plan.groupName.toUpperCase() ===
      CATALOGUE.PLANS_GROUP_NAME.SIM_ONLY_PLUS.toUpperCase()
  );

  // Add the variants that contain installment prices to the phones array
  const phones = flattenNodes(allPhone)
    .filter(phone => !(phone.isRoi === true || phone.groupCampaignId))
    .map(phone => {
      const { variants } = phone;

      // Get all the plans that are part of the Sim Only Plus group
      const simOnlyPlusPlans = allSimOnlyPlusPlan.map(({ mecId }) => mecId);

      // Get the variants that are only part of the Sim Only Plus plans
      const simOnlyPlusVariants = variants.filter(({ planId }) =>
        simOnlyPlusPlans.includes(planId)
      );

      return {
        ...phone,
        variants: simOnlyPlusVariants
      };
    });

  let phonePlanFilters = allPlan
    .filter(plan => plan.enabledOnPhoneCatalog)
    .map(plan => ({
      ...plan,
      text: plan.planName,
      value: plan.mecId,
      selected: plan.selected
    }));

  // filter and overwrite plan prices if customer is on cis page
  if (cisPlanRates) {
    phonePlanFilters = phonePlanFilters
      .map(p => {
        const matched = cisPlanRates.find(
          cisPlan => cisPlan.planID === p.basePriceSchemaId
        );

        if (matched) {
          return { ...p, monthlyCharges: matched.discountedPrice };
        }
        return undefined;
      })
      .filter(p => p);
  }

  const queryParamFilters = getFiltersFromQueryParams(
    phonePlanFilters,
    allSimOnlyPlusPlan,
    location.search
  );
  // if the url has a plan query,
  // then use the plan from query as the default filter.
  const defaultFilter =
    phonePlanFilters.find(f => f.selected) || phonePlanFilters[0];

  // Selected filter should always be selectedPlan in the store if it is defined.
  const selectedPhonePlanFilter =
    selectedPlan && phonePlanFilters.find(f => f.mecId === selectedPlan.mecId);

  const selectedFilter = selectedPhonePlanFilter || defaultFilter;
  const dispatch = useDispatch();
  const [filteredPhones, setFilteredPhones] = useState(phones);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const phonesWithPrice = getPhonesWithPrice(
    filteredPhones,
    selectedFilter,
    selectedPlan
  );
  const sortedPhones = phonesWithPrice.sort(sortByRankingAndTitle);

  useEffect(() => {
    if (authStatus) {
      deleteWorryFree();
      resetByKeys(['user', 'auth', 'product']);
    } else {
      resetByKeys(['user', 'auth', 'product']);
    }
    dispatch({ type: APP_ACTION_TYPES.ORDER.RESET_ORDER });
    dispatch({ type: APP_ACTION_TYPES.DELIVERY.CLEAR_DELIVERY });
  }, [authStatus, deleteWorryFree, resetByKeys]);

  /**
   * Set Selected Plan for phone catalog
   */
  useEffect(() => {
    const _selectedPlan = getPricePlan();
    setSelectedPlan(_selectedPlan);

    // Update plan query param in url.
    // in cis-phones page, validateSession will be called and the code below calling navigate({replace: true}) simultaneously will cancel the validateToken request.
    if (!isCisPage || (isCisPage && cisPlanRates?.length)) {
      _selectedPlan &&
        updateQueryFilters(location, { plan: slugify(_selectedPlan.planName) });
    }
  }, []);

  const getPricePlan = () => {
    let queryParams = getUrlParams(location.search);
    let _selectedPlan = stripValues(defaultFilter);

    /**
     * (1) First priority
     * If user has a selection made (in PhoneDetail),
     * then use selected plan here, in PhoneCatalog,
     *
     * Note that userSelection is not stored in session storage.
     */
    if (userSelection?.plan && selectedPlan) {
      // sets default plan to the store
      _selectedPlan = selectedPlan;
    } else if (queryParams.plan) {
      /**
       * (2) Second priority
       * If a valid "plan" query param exists in the url,
       * use this query plan as the selected plan.
       */
      if (queryParamFilters.plan) {
        // Use ?plan query
        _selectedPlan = stripValues(queryParamFilters.plan);
      } else {
        // Use default filter if, invalid or missing plan="..." value
      }
    }

    if (!phonePlanFilters.find(f => f.value === _selectedPlan?.mecId)) {
      // if we cannot find any filter from selectedPlan, we default selectedPlan to undefined
      _selectedPlan = defaultFilter;
    }
    return _selectedPlan;
  };

  const resetDefaultPlan = () => {
    const defaultPlan = stripValues(defaultFilter);
    selectPlanAndUpdateUrl(defaultPlan);
  };

  const selectPlanAndUpdateUrl = plan => {
    const url = getUrlByPlan(plan);
    if (url) {
      window.history.replaceState(null, null, url);
      setSelectedPlan(plan);
    }
  };

  const handleFilterChange = (phonesFilter = [], ippFilter = {}) => {
    const filtered = phones.filter(phone => {
      return phonesFilter.includes(phone.slug);
    });
    setFilteredPhones(filtered);

    if (ippFilter[FILTERS.IPP_OPTIONS] !== undefined) {
      if (ippFilter[FILTERS.IPP_OPTIONS]) {
        if (allSimOnlyPlusPlan && allSimOnlyPlusPlan.length) {
          const defaultSimOnlyPlusPlan = allSimOnlyPlusPlan[0];
          if (isSimOnlyPlan(selectedPlan)) {
            let formatSelectedPlan = {
              ...selectedPlan,
              selectedMonthlyTerm:
                selectedPlan.selectedMonthlyTerm ||
                CATALOGUE.INSTALLMENT_MONTHS[0]
            };
            selectPlanAndUpdateUrl(formatSelectedPlan);
          } else {
            defaultSimOnlyPlusPlan.selectedMonthlyTerm =
              CATALOGUE.INSTALLMENT_MONTHS[0];
            selectPlanAndUpdateUrl(defaultSimOnlyPlusPlan);
          }
        }
      } else {
        if (isSimOnlyPlan(selectedPlan)) {
          resetDefaultPlan();
        } else {
          selectPlanAndUpdateUrl(selectedPlan);
        }
      }
    } else {
      const newSelectedPlan = getPricePlan();
      setSelectedPlan(newSelectedPlan);
    }
  };

  const promoStrip = getAemComponentByName(allAemPages, 'PromoStrip');
  const promoBanner = getAemComponentByName(allAemPages, 'PromoBanner');

  const onCardClick = () => {
    clearSelectedAccessories();
  };

  return (
    <Fragment>
      {cisLoading && <LoadingOverlay />}
      <Spacing top={-5} topMd={-9}>
        <Spacing bottom={5} bottomLg={3}>
          {promoStrip && promoStrip.enablePromoStrip && (
            <PromoStrip
              primaryLiner={promoStrip.primaryLiner}
              enableSecondaryStrip={promoStrip.enableSecondaryStrip}
              modalContent={{
                mainTitle: promoStrip.mainTitle,
                sectionTitle: promoStrip.sectionTitle,
                sectionDescription: promoStrip.sectionDescription,
                sectionFooter: promoStrip.sectionFooter
              }}
            />
          )}
          {promoBanner && promoBanner.enablePromoBanner && (
            <PromoBanner
              bannerTheme={promoBanner.theme}
              desktopImg={
                <GatsbyImage
                  image={
                    promoBanner?.desktopImg?.childImageSharp?.gatsbyImageData
                  }
                  alt={promoBanner.desktopImgAlt}
                />
              }
              mobileImg={
                <GatsbyImage
                  image={
                    promoBanner?.mobileImg?.childImageSharp?.gatsbyImageData
                  }
                  alt={promoBanner.mobileImgAlt}
                />
              }
              title={promoBanner.title}
              description={promoBanner.description}
              enableCta={promoBanner.enableCTA}
              ctaText={promoBanner.ctaText}
              modalTitle={promoBanner.sectionTitle}
              modalDescription={promoBanner.sectionDescription}
              desktopFileReference={promoBanner.desktopFileReference}
              mobileFileReference={promoBanner.mobileFileReference}
              previewMode={previewMode}
            />
          )}
          {!cisLoading && !cisPlanRates && <CisPointOfEntry />}
        </Spacing>
        <Grid>
          <Row>
            <Column>
              <Spacing bottom={0} bottomLg={3}>
                <H1>Phones & more</H1>
              </Spacing>
            </Column>
          </Row>
        </Grid>
        <StyledSticky
          stickyStyle={{
            boxShadow: `0 2px 5px 0 ${Theme.colours.grey_400}`,
            backgroundColor: `${Theme.colours.white}`
          }}
        >
          <Grid>
            <Row>
              <Column sm={12} lg={false}>
                <Spacing top={2} bottom={2}>
                  <StyledFilterButton
                    onClick={() => setShowFilterModal(!showFilterModal)}
                  >
                    Filter <StyledIconFilter width={20} height={20} />
                  </StyledFilterButton>
                </Spacing>
              </Column>
            </Row>
          </Grid>
        </StyledSticky>
        {FEATURE_DISABLED && (
          <StyledSticky
            stickyStyle={{ boxShadow: `0 2px 5px 0 ${Theme.colours.grey_400}` }}
          >
            <Grid>
              <Row middle="sm">
                <Column sm={12}>
                  <Spacing top={2} bottom={2} bottomMd={3}>
                    <PersistStatusConsumer>
                      <PlanSelect
                        defaultPlanId={defaultFilter.mecId}
                        selectLabel="Price with"
                        items={phonePlanFilters}
                        selectedItem={withTextAndValue(selectedFilter)}
                        showFilters={true}
                        summary={`${createDescription(
                          selectedFilter
                        )} – ${formatPrice(selectedFilter.monthlyCharges)}/mth`}
                        onFilterButtonClick={() =>
                          setShowFilterModal(!showFilterModal)
                        }
                        onSelectChange={plan =>
                          setSelectedPlan(stripValues(plan))
                        }
                      />
                    </PersistStatusConsumer>
                  </Spacing>
                </Column>
              </Row>
            </Grid>
          </StyledSticky>
        )}
        <StyledSection>
          <StyledGrid>
            <StyledRow>
              <StyledColumn sm={12} md={4} lg={3}>
                <PhoneCatalogFilterSection
                  defaultFilter={defaultFilter}
                  allCategories={allPhoneCatalogCategory}
                  allBrands={allPhoneBrand}
                  allInstalmentOptions={allInstalmentOptions}
                  cisPlanRates={cisPlanRates}
                  phones={phones}
                  showFilterModal={showFilterModal}
                  onFilterModalToggle={setShowFilterModal}
                  onFilterChange={handleFilterChange}
                  selectedPlan={selectedPlan}
                  location={location}
                />
              </StyledColumn>
              <StyledColumn>
                <PhonesList
                  selectedPlan={selectedPlan}
                  onCardClick={onCardClick}
                  phones={sortedPhones}
                />
              </StyledColumn>
            </StyledRow>
          </StyledGrid>
        </StyledSection>
      </Spacing>
    </Fragment>
  );
};

PhoneCatalog.defaultProps = {
  setSelectedPlan: () => {},
  resetByKeys: () => {},
  location: {
    pathname: '/',
    origin: '',
    search: '',
    href: ''
  }
};

PhoneCatalog.propTypes = {
  data: PropTypes.object,
  location: PropTypes.object,
  selectedPlan: PropTypes.object,
  setSelectedPlan: PropTypes.func,
  authStatus: PropTypes.string,
  deleteWorryFree: PropTypes.func,
  resetByKeys: PropTypes.func,
  cisPlanRates: PropTypes.arrayOf(
    PropTypes.shape({
      planID: PropTypes.string,
      discountedPrice: PropTypes.string
    })
  ),
  cisLoading: PropTypes.bool
};

/* istanbul ignore next */
const mapStateToProps = state => {
  const { plan, auth, user } = state;
  return {
    userSelection: user.selection,
    selectedPlan: plan.selectedPlan,
    authStatus: auth.worryFree.status,
    cisPlanRates: user.cis.information && user.cis.information.rates,
    cisLoading: user.cis && user.cis.loading
  };
};

/* istanbul ignore next */
const mapDispatchToProps = dispatch => {
  const { setSelectedPlan } = planActions;
  const { deleteWorryFree } = authActions;
  const { resetByKeys } = rootActions;
  const { clearSelectedAccessories } = accessoryActions;

  return {
    setSelectedPlan: plan => dispatch(setSelectedPlan(plan)),
    deleteWorryFree: () => dispatch(deleteWorryFree()),
    resetByKeys: keys => dispatch(resetByKeys(keys)),
    clearSelectedAccessories: () => dispatch(clearSelectedAccessories())
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(PhoneCatalog);
